import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
    Users, Search, Plus, X, Phone, Mail, Globe, Instagram, Linkedin,
    Twitter, Edit2, Trash2, Settings, Upload, Share2, Copy, Check,
    Building2, MapPin, Tag, FileText, ExternalLink, Lock, Eye, Smartphone
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

const ContactsPage = () => {
    const { api, user } = useAuth();
    const [contacts, setContacts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [shares, setShares] = useState([]);
    const [loading, setLoading] = useState(true);

    const [searchTerm, setSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('__all__');
    const [activeTab, setActiveTab] = useState('contacts');

    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showCategoriesModal, setShowCategoriesModal] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [editingContact, setEditingContact] = useState(null);
    const [detailContact, setDetailContact] = useState(null);
    const [copiedId, setCopiedId] = useState(null);

    const [newContact, setNewContact] = useState({
        name: '', company: '', position: '',
        phones: [{ type: 'mobile', number: '' }],
        emails: [{ type: 'work', email: '' }],
        socials: [],
        address: '', notes: '', categories: [], referred_by: ''
    });

    const [newShare, setNewShare] = useState({
        name: '', contact_ids: null, password: '', usePassword: false, selectedContacts: []
    });

    const [newCategory, setNewCategory] = useState('');
    const [importSupported, setImportSupported] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    const isAdmin = user?.role === 'chef' || user?.role === 'admin';

    useEffect(() => {
        const mobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        setIsMobile(mobile);
        // Contact Picker API: only check 'contacts' in navigator (no ContactsManager check)
        setImportSupported('contacts' in navigator);
    }, []);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);

            const params = new URLSearchParams();
            if (searchTerm) params.append('search', searchTerm);
            if (categoryFilter && categoryFilter !== '__all__') params.append('category', categoryFilter);

            const [contactsRes, categoriesRes, sharesRes] = await Promise.all([
                api.get(`/contacts?${params.toString()}`),
                api.get('/contacts/categories'),
                isAdmin ? api.get('/contacts/shares').catch(() => ({ data: [] })) : Promise.resolve({ data: [] })
            ]);

            setContacts(contactsRes.data || []);
            setCategories(categoriesRes.data || []);
            setShares(sharesRes.data || []);
        } catch (err) {
            console.error('Failed to fetch contacts:', err);
            toast.error('Fehler beim Laden');
        } finally {
            setLoading(false);
        }
    }, [api, searchTerm, categoryFilter, isAdmin]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Import from device (Contact Picker API)
    const handleDeviceImport = async () => {
        try {
            const props = ['name', 'email', 'tel', 'address', 'organization'];
            const opts = { multiple: true };
            const selectedContacts = await navigator.contacts.select(props, opts);

            if (selectedContacts.length === 0) {
                toast.info('Keine Kontakte ausgewahlt');
                return;
            }

            const res = await api.post('/contacts/import', { contacts: selectedContacts });
            toast.success(`${res.data.imported} Kontakte importiert!`);
            setShowImportModal(false);
            fetchData();
        } catch (err) {
            if (err.name === 'InvalidStateError') {
                toast.error('Kontakt-Zugriff wurde verweigert');
            } else {
                console.error('Import error:', err);
                toast.error('Import fehlgeschlagen');
            }
        }
    };

    // Import vCard file
    const handleFileImport = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        try {
            const formData = new FormData();
            formData.append('file', file);

            const res = await api.post('/contacts/import-vcard', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            toast.success(`${res.data.imported} Kontakte importiert!`);
            setShowImportModal(false);
            fetchData();
        } catch (err) {
            toast.error('Import fehlgeschlagen');
        }
    };

    // Create contact
    const handleCreate = async () => {
        if (!newContact.name.trim()) {
            toast.error('Name ist erforderlich');
            return;
        }

        try {
            const data = {
                ...newContact,
                phones: newContact.phones.filter(p => p.number.trim()),
                emails: newContact.emails.filter(e => e.email.trim()),
                socials: newContact.socials.filter(s => s.url.trim()),
                referred_by: newContact.referred_by || null
            };

            await api.post('/contacts', data);
            toast.success('Kontakt erstellt!');
            setShowAddModal(false);
            resetNewContact();
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Fehler');
        }
    };

    const resetNewContact = () => {
        setNewContact({
            name: '', company: '', position: '',
            phones: [{ type: 'mobile', number: '' }],
            emails: [{ type: 'work', email: '' }],
            socials: [], address: '', notes: '', categories: [], referred_by: ''
        });
    };

    // Update contact
    const handleUpdate = async () => {
        if (!editingContact?.name?.trim()) {
            toast.error('Name ist erforderlich');
            return;
        }

        try {
            const updateData = { ...editingContact };
            // referred_by: send "" to remove, or the id to set
            if (updateData.referred_by === '' || updateData.referred_by === null) {
                updateData.referred_by = '';
            }
            await api.put(`/contacts/${editingContact.id}`, updateData);
            toast.success('Gespeichert!');
            setShowEditModal(false);
            setEditingContact(null);
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Fehler');
        }
    };

    // Delete contact
    const handleDelete = async (id) => {
        if (!window.confirm('Kontakt loschen?')) return;

        try {
            await api.delete(`/contacts/${id}`);
            toast.success('Geloscht!');
            fetchData();
        } catch (err) {
            toast.error('Fehler');
        }
    };

    // Create share link
    const handleCreateShare = async () => {
        if (!newShare.name.trim()) {
            toast.error('Name ist erforderlich');
            return;
        }

        try {
            const data = {
                name: newShare.name,
                contact_ids: newShare.selectedContacts.length > 0 ? newShare.selectedContacts : null,
                password: newShare.usePassword ? newShare.password : null
            };

            const res = await api.post('/contacts/shares', data);
            const shareUrl = `${window.location.origin}${res.data.share.url}`;

            navigator.clipboard.writeText(shareUrl);
            toast.success('Link erstellt und kopiert!');
            setShowShareModal(false);
            setNewShare({ name: '', contact_ids: null, password: '', usePassword: false, selectedContacts: [] });
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Fehler');
        }
    };

    const handleDeleteShare = async (id) => {
        if (!window.confirm('Link loschen?')) return;

        try {
            await api.delete(`/contacts/shares/${id}`);
            toast.success('Geloscht!');
            fetchData();
        } catch (err) {
            toast.error('Fehler');
        }
    };

    const copyShareLink = (token) => {
        const url = `${window.location.origin}/shared-contacts/${token}`;
        navigator.clipboard.writeText(url);
        setCopiedId(token);
        toast.success('Link kopiert!');
        setTimeout(() => setCopiedId(null), 2000);
    };

    // Category management
    const handleCreateCategory = async () => {
        if (!newCategory.trim()) return;

        try {
            await api.post('/contacts/categories', { name: newCategory.trim() });
            toast.success('Kategorie erstellt!');
            setNewCategory('');
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Fehler');
        }
    };

    const handleDeleteCategory = async (name) => {
        if (!window.confirm(`Kategorie "${name}" loschen?`)) return;

        try {
            await api.delete(`/contacts/categories/${encodeURIComponent(name)}`);
            toast.success('Geloscht!');
            fetchData();
        } catch (err) {
            toast.error('Fehler');
        }
    };

    const addPhone = (setter, obj) => setter({ ...obj, phones: [...(obj.phones || []), { type: 'mobile', number: '' }] });
    const addEmail = (setter, obj) => setter({ ...obj, emails: [...(obj.emails || []), { type: 'work', email: '' }] });

    const getSocialIcon = (platform) => {
        switch (platform) {
            case 'instagram': return <Instagram className="w-4 h-4" />;
            case 'linkedin': return <Linkedin className="w-4 h-4" />;
            case 'twitter': return <Twitter className="w-4 h-4" />;
            case 'website': return <Globe className="w-4 h-4" />;
            default: return <Globe className="w-4 h-4" />;
        }
    };

    const getCategoryColor = (catName) => {
        const cat = categories.find(c => c.name === catName);
        return cat?.color || '#6366F1';
    };

    // Open detail modal with enriched referral data
    const openContactDetail = async (contact) => {
        setDetailContact(contact);
        setShowDetailModal(true);
        try {
            const res = await api.get(`/contacts/${contact.id}`);
            setDetailContact(res.data);
        } catch (err) {
            // Keep basic data if enrichment fails
        }
    };

    // Contact card component
    const ContactCard = ({ contact }) => (
        <Card
            data-testid={`contact-card-${contact.id}`}
            className="group hover:shadow-md transition-all cursor-pointer active:scale-[0.98]"
            onClick={() => openContactDetail(contact)}
        >
            <CardContent className="p-3 sm:p-4">
                <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div
                            className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-sm flex-shrink-0"
                            style={{ backgroundColor: getCategoryColor(contact.categories?.[0]) }}
                        >
                            {contact.name?.charAt(0)?.toUpperCase()}
                        </div>
                        <div className="min-w-0">
                            <h3 className="font-semibold truncate text-sm sm:text-base">{contact.name}</h3>
                            {(contact.company || contact.position) && (
                                <p className="text-xs sm:text-sm text-muted-foreground truncate">
                                    {contact.position}{contact.position && contact.company && ' - '}{contact.company}
                                </p>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center gap-1 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity flex-shrink-0">
                        <Button
                            data-testid={`edit-contact-${contact.id}`}
                            variant="ghost" size="icon" className="h-7 w-7"
                            onClick={(e) => { e.stopPropagation(); setEditingContact({...contact}); setShowEditModal(true); }}
                        >
                            <Edit2 className="w-3 h-3" />
                        </Button>
                        <Button
                            data-testid={`delete-contact-${contact.id}`}
                            variant="ghost" size="icon" className="h-7 w-7 text-destructive"
                            onClick={(e) => { e.stopPropagation(); handleDelete(contact.id); }}
                        >
                            <Trash2 className="w-3 h-3" />
                        </Button>
                    </div>
                </div>

                <div className="mt-2 space-y-0.5 text-xs sm:text-sm">
                    {contact.phones?.slice(0, 1).map((p, i) => (
                        <a key={i} href={`tel:${p.number}`} className="flex items-center gap-2 text-muted-foreground hover:text-primary"
                            onClick={(e) => e.stopPropagation()}>
                            <Phone className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">{p.number}</span>
                        </a>
                    ))}
                    {contact.emails?.slice(0, 1).map((e, i) => (
                        <a key={i} href={`mailto:${e.email}`} className="flex items-center gap-2 text-muted-foreground hover:text-primary"
                            onClick={(ev) => ev.stopPropagation()}>
                            <Mail className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">{e.email}</span>
                        </a>
                    ))}
                </div>

                {contact.categories?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                        {contact.categories.slice(0, 2).map(cat => (
                            <span
                                key={cat}
                                className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium text-white"
                                style={{ backgroundColor: getCategoryColor(cat) }}
                            >
                                {cat}
                            </span>
                        ))}
                        {contact.categories.length > 2 && (
                            <span className="text-[10px] text-muted-foreground">+{contact.categories.length - 2}</span>
                        )}
                    </div>
                )}
            </CardContent>
        </Card>
    );

    // Contact form fields (reused in Add/Edit)
    const ContactFormFields = ({ data, setData, isEdit = false }) => (
        <div className="space-y-3">
            <div>
                <Label className="text-xs">Name *</Label>
                <Input data-testid="contact-name-input" value={data.name || ''} onChange={(e) => setData({ ...data, name: e.target.value })} placeholder="Max Mustermann" />
            </div>

            <div className="grid grid-cols-2 gap-2">
                <div>
                    <Label className="text-xs">Firma</Label>
                    <Input data-testid="contact-company-input" value={data.company || ''} onChange={(e) => setData({ ...data, company: e.target.value })} placeholder="Firma GmbH" />
                </div>
                <div>
                    <Label className="text-xs">Position</Label>
                    <Input data-testid="contact-position-input" value={data.position || ''} onChange={(e) => setData({ ...data, position: e.target.value })} placeholder="Manager" />
                </div>
            </div>

            {/* Phones */}
            <div>
                <div className="flex items-center justify-between mb-1">
                    <Label className="text-xs">Telefon</Label>
                    <Button type="button" variant="ghost" size="sm" className="h-6 px-2"
                        onClick={() => addPhone(setData, data)}><Plus className="w-3 h-3" /></Button>
                </div>
                {(data.phones || []).map((p, i) => (
                    <div key={i} className="flex gap-1.5 mb-1.5">
                        <Select value={p.type} onValueChange={(v) => {
                            const phones = [...(data.phones || [])];
                            phones[i] = { ...phones[i], type: v };
                            setData({ ...data, phones });
                        }}>
                            <SelectTrigger className="w-20 h-9 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="mobile">Mobil</SelectItem>
                                <SelectItem value="work">Arbeit</SelectItem>
                                <SelectItem value="home">Privat</SelectItem>
                            </SelectContent>
                        </Select>
                        <Input value={p.number} className="flex-1 h-9" onChange={(e) => {
                            const phones = [...(data.phones || [])];
                            phones[i] = { ...phones[i], number: e.target.value };
                            setData({ ...data, phones });
                        }} placeholder="+43..." />
                        {(data.phones || []).length > 1 && (
                            <Button variant="ghost" size="icon" className="h-9 w-9 flex-shrink-0" onClick={() => {
                                setData({ ...data, phones: (data.phones || []).filter((_, idx) => idx !== i) });
                            }}><X className="w-3 h-3" /></Button>
                        )}
                    </div>
                ))}
            </div>

            {/* Emails */}
            <div>
                <div className="flex items-center justify-between mb-1">
                    <Label className="text-xs">E-Mail</Label>
                    <Button type="button" variant="ghost" size="sm" className="h-6 px-2"
                        onClick={() => addEmail(setData, data)}><Plus className="w-3 h-3" /></Button>
                </div>
                {(data.emails || []).map((e, i) => (
                    <div key={i} className="flex gap-1.5 mb-1.5">
                        <Select value={e.type} onValueChange={(v) => {
                            const emails = [...(data.emails || [])];
                            emails[i] = { ...emails[i], type: v };
                            setData({ ...data, emails });
                        }}>
                            <SelectTrigger className="w-20 h-9 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="work">Arbeit</SelectItem>
                                <SelectItem value="personal">Privat</SelectItem>
                            </SelectContent>
                        </Select>
                        <Input type="email" value={e.email} className="flex-1 h-9" onChange={(ev) => {
                            const emails = [...(data.emails || [])];
                            emails[i] = { ...emails[i], email: ev.target.value };
                            setData({ ...data, emails });
                        }} placeholder="email@beispiel.de" />
                        {(data.emails || []).length > 1 && (
                            <Button variant="ghost" size="icon" className="h-9 w-9 flex-shrink-0" onClick={() => {
                                setData({ ...data, emails: (data.emails || []).filter((_, idx) => idx !== i) });
                            }}><X className="w-3 h-3" /></Button>
                        )}
                    </div>
                ))}
            </div>

            {/* Socials */}
            <div>
                <Label className="text-xs">Social / Website</Label>
                <div className="flex gap-1.5 mb-1.5 flex-wrap">
                    {['instagram', 'linkedin', 'website'].map(p => (
                        <Button key={p} type="button" variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => {
                            setData({ ...data, socials: [...(data.socials || []), { platform: p, url: '' }] });
                        }}>
                            {getSocialIcon(p)} {p === 'website' ? 'Web' : p.charAt(0).toUpperCase() + p.slice(1)}
                        </Button>
                    ))}
                </div>
                {(data.socials || []).map((s, i) => (
                    <div key={i} className="flex gap-1.5 mb-1.5 items-center">
                        {getSocialIcon(s.platform)}
                        <Input value={s.url} className="flex-1 h-9" onChange={(e) => {
                            const socials = [...(data.socials || [])];
                            socials[i] = { ...socials[i], url: e.target.value };
                            setData({ ...data, socials });
                        }} placeholder="URL..." />
                        <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => {
                            setData({ ...data, socials: (data.socials || []).filter((_, idx) => idx !== i) });
                        }}><X className="w-3 h-3" /></Button>
                    </div>
                ))}
            </div>

            {/* Address */}
            <div>
                <Label className="text-xs">Adresse</Label>
                <Input value={data.address || ''} onChange={(e) => setData({ ...data, address: e.target.value })} placeholder="Strasse, PLZ Ort" />
            </div>

            {/* Categories */}
            <div>
                <Label className="text-xs">Kategorien</Label>
                <div className="flex flex-wrap gap-1 mt-1 max-h-24 overflow-y-auto">
                    {categories.map(cat => (
                        <button
                            key={cat.name}
                            type="button"
                            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium transition-all ${
                                (data.categories || []).includes(cat.name)
                                    ? 'text-white shadow-sm'
                                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                            }`}
                            style={(data.categories || []).includes(cat.name) ? { backgroundColor: cat.color } : {}}
                            onClick={() => {
                                const cats = (data.categories || []).includes(cat.name)
                                    ? (data.categories || []).filter(c => c !== cat.name)
                                    : [...(data.categories || []), cat.name];
                                setData({ ...data, categories: cats });
                            }}
                        >
                            {cat.name}
                        </button>
                    ))}
                </div>
            </div>

            {/* Referred By */}
            <div>
                <Label className="text-xs">Kontakt erhalten von</Label>
                <Select
                    value={data.referred_by || '__none__'}
                    onValueChange={(v) => setData({ ...data, referred_by: v === '__none__' ? '' : v })}
                >
                    <SelectTrigger className="h-9 text-sm" data-testid="referred-by-select">
                        <SelectValue placeholder="Kein Verweis" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="__none__">Kein Verweis</SelectItem>
                        {contacts
                            .filter(c => c.id !== data.id) // Don't allow self-reference
                            .map(c => (
                                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                            ))
                        }
                    </SelectContent>
                </Select>
                <p className="text-[10px] text-muted-foreground mt-0.5">Von wem hast du diesen Kontakt bekommen?</p>
            </div>

            {/* Notes */}
            <div>
                <Label className="text-xs">Notizen</Label>
                <Textarea value={data.notes || ''} onChange={(e) => setData({ ...data, notes: e.target.value })} rows={2} className="text-sm" />
            </div>
        </div>
    );

    return (
        <div className="h-full bg-background">
            <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 sm:py-6 space-y-4">
                {/* Header - compact on mobile */}
                <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 sm:gap-3">
                        <div className="w-9 h-9 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center shadow-lg">
                            <Users className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                        </div>
                        <div>
                            <h1 className="text-lg sm:text-2xl font-bold" data-testid="contacts-title">Kontakte</h1>
                            <p className="text-xs sm:text-sm text-muted-foreground">{contacts.length} Kontakte</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-1.5 sm:gap-2">
                        {isAdmin && (
                            <Button data-testid="categories-btn" variant="outline" size="icon" className="h-8 w-8 sm:h-9 sm:w-9"
                                onClick={() => setShowCategoriesModal(true)} title="Kategorien">
                                <Settings className="w-4 h-4" />
                            </Button>
                        )}
                        {isAdmin && (
                            <Button data-testid="share-btn" variant="outline" size="sm" className="h-8 sm:h-9 gap-1 text-xs sm:text-sm"
                                onClick={() => setShowShareModal(true)}>
                                <Share2 className="w-3.5 h-3.5" />
                                <span className="hidden sm:inline">Teilen</span>
                            </Button>
                        )}
                        <Button data-testid="import-btn" variant="outline" size="sm" className="h-8 sm:h-9 gap-1 text-xs sm:text-sm"
                            onClick={() => setShowImportModal(true)}>
                            <Upload className="w-3.5 h-3.5" />
                            <span className="hidden sm:inline">Import</span>
                        </Button>
                        <Button data-testid="add-contact-btn" size="sm" className="h-8 sm:h-9 gap-1 text-xs sm:text-sm"
                            onClick={() => { resetNewContact(); setShowAddModal(true); }}>
                            <Plus className="w-3.5 h-3.5" />
                            <span className="hidden sm:inline">Kontakt</span>
                        </Button>
                    </div>
                </div>

                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="h-8 sm:h-9">
                        <TabsTrigger value="contacts" className="text-xs sm:text-sm" data-testid="tab-contacts">Kontakte</TabsTrigger>
                        {isAdmin && <TabsTrigger value="shares" className="text-xs sm:text-sm" data-testid="tab-shares">Geteilte Links</TabsTrigger>}
                    </TabsList>

                    <TabsContent value="contacts" className="space-y-3 mt-3">
                        {/* Search & Filter */}
                        <div className="flex gap-2">
                            <div className="relative flex-1">
                                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input
                                    data-testid="search-contacts"
                                    placeholder="Suchen..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-9 h-9"
                                />
                            </div>
                            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                                <SelectTrigger data-testid="category-filter" className="w-[140px] sm:w-[180px] h-9 text-xs sm:text-sm">
                                    <SelectValue placeholder="Alle" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__all__">Alle Kategorien</SelectItem>
                                    {categories.map(cat => (
                                        <SelectItem key={cat.name} value={cat.name}>
                                            <span className="flex items-center gap-2">
                                                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: cat.color }} />
                                                {cat.name}
                                            </span>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Contacts List */}
                        {loading ? (
                            <div className="text-center py-12">
                                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
                            </div>
                        ) : contacts.length === 0 ? (
                            <div className="text-center py-12" data-testid="no-contacts">
                                <Users className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                                <p className="text-muted-foreground">Keine Kontakte</p>
                                <Button variant="outline" className="mt-4" onClick={() => setShowImportModal(true)}>
                                    Kontakte importieren
                                </Button>
                            </div>
                        ) : (
                            <div className="grid gap-2 sm:gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                                {contacts.map(contact => (
                                    <ContactCard key={contact.id} contact={contact} />
                                ))}
                            </div>
                        )}
                    </TabsContent>

                    {isAdmin && (
                        <TabsContent value="shares" className="space-y-3 mt-3">
                            {shares.length === 0 ? (
                                <div className="text-center py-12" data-testid="no-shares">
                                    <Share2 className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                                    <p className="text-muted-foreground">Keine geteilten Links</p>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {shares.map(share => (
                                        <Card key={share.id} data-testid={`share-card-${share.id}`}>
                                            <CardContent className="p-3 sm:p-4 flex items-center justify-between gap-2">
                                                <div className="min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <h3 className="font-medium text-sm truncate">{share.name}</h3>
                                                        {share.has_password && <Lock className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />}
                                                    </div>
                                                    <p className="text-xs text-muted-foreground">
                                                        {share.contact_ids ? `${share.contact_ids.length} Kontakte` : 'Alle Kontakte'} - {share.views || 0} Aufrufe
                                                    </p>
                                                </div>
                                                <div className="flex items-center gap-1 flex-shrink-0">
                                                    <Button variant="outline" size="sm" className="h-7 px-2"
                                                        onClick={() => copyShareLink(share.token)}>
                                                        {copiedId === share.token ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                                                    </Button>
                                                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive"
                                                        onClick={() => handleDeleteShare(share.id)}>
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                    </Button>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </div>
                            )}
                        </TabsContent>
                    )}
                </Tabs>
            </div>

            {/* ===== DETAIL MODAL (mobile-optimized) ===== */}
            <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
                <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto p-4 sm:p-6">
                    <DialogHeader className="pb-2">
                        <DialogTitle className="flex items-center gap-3">
                            {detailContact && (
                                <>
                                    <div
                                        className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold"
                                        style={{ backgroundColor: getCategoryColor(detailContact.categories?.[0]) }}
                                    >
                                        {detailContact.name?.charAt(0)?.toUpperCase()}
                                    </div>
                                    <div>
                                        <div className="text-base">{detailContact.name}</div>
                                        {(detailContact.position || detailContact.company) && (
                                            <div className="text-xs text-muted-foreground font-normal">
                                                {detailContact.position}{detailContact.position && detailContact.company && ' - '}{detailContact.company}
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </DialogTitle>
                    </DialogHeader>

                    {detailContact && (
                        <div className="space-y-3">
                            {/* Phones */}
                            {detailContact.phones?.length > 0 && (
                                <div className="space-y-1">
                                    {detailContact.phones.map((p, i) => (
                                        <a key={i} href={`tel:${p.number}`}
                                            className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted transition-colors">
                                            <Phone className="w-4 h-4 text-primary" />
                                            <div>
                                                <div className="text-sm">{p.number}</div>
                                                <div className="text-xs text-muted-foreground capitalize">{p.type === 'mobile' ? 'Mobil' : p.type === 'work' ? 'Arbeit' : 'Privat'}</div>
                                            </div>
                                        </a>
                                    ))}
                                </div>
                            )}

                            {/* Emails */}
                            {detailContact.emails?.length > 0 && (
                                <div className="space-y-1">
                                    {detailContact.emails.map((e, i) => (
                                        <a key={i} href={`mailto:${e.email}`}
                                            className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted transition-colors">
                                            <Mail className="w-4 h-4 text-primary" />
                                            <div className="min-w-0">
                                                <div className="text-sm truncate">{e.email}</div>
                                                <div className="text-xs text-muted-foreground">{e.type === 'work' ? 'Arbeit' : 'Privat'}</div>
                                            </div>
                                        </a>
                                    ))}
                                </div>
                            )}

                            {/* Socials */}
                            {detailContact.socials?.length > 0 && (
                                <div className="space-y-1">
                                    {detailContact.socials.map((s, i) => (
                                        <a key={i} href={s.url} target="_blank" rel="noopener noreferrer"
                                            className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted transition-colors">
                                            {getSocialIcon(s.platform)}
                                            <div className="text-sm truncate">{s.url}</div>
                                            <ExternalLink className="w-3 h-3 text-muted-foreground ml-auto flex-shrink-0" />
                                        </a>
                                    ))}
                                </div>
                            )}

                            {/* Address */}
                            {detailContact.address && (
                                <div className="flex items-center gap-3 p-2 rounded-lg">
                                    <MapPin className="w-4 h-4 text-primary flex-shrink-0" />
                                    <div className="text-sm">{detailContact.address}</div>
                                </div>
                            )}

                            {/* Categories */}
                            {detailContact.categories?.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 py-1">
                                    {detailContact.categories.map(cat => (
                                        <span
                                            key={cat}
                                            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium text-white"
                                            style={{ backgroundColor: getCategoryColor(cat) }}
                                        >
                                            {cat}
                                        </span>
                                    ))}
                                </div>
                            )}

                            {/* Notes */}
                            {detailContact.notes && (
                                <div className="p-2 bg-muted/50 rounded-lg">
                                    <div className="text-xs text-muted-foreground mb-1">Notizen</div>
                                    <div className="text-sm whitespace-pre-wrap">{detailContact.notes}</div>
                                </div>
                            )}

                            {/* Referral Tree / Kontaktstammbaum */}
                            {(detailContact.referred_by_contact || detailContact.referrals?.length > 0) && (
                                <div className="p-2.5 rounded-lg bg-gradient-to-br from-blue-500/5 to-cyan-500/5 border border-blue-500/10 space-y-2">
                                    <div className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5">
                                        <Users className="w-3.5 h-3.5" />
                                        Kontaktstammbaum
                                    </div>

                                    {/* Referred by */}
                                    {detailContact.referred_by_contact && (
                                        <div className="flex items-center gap-2">
                                            <span className="text-[11px] text-muted-foreground w-24 flex-shrink-0">Erhalten von:</span>
                                            <button
                                                className="text-sm font-medium text-primary hover:underline flex items-center gap-1.5 truncate"
                                                onClick={() => openContactDetail(detailContact.referred_by_contact)}
                                                data-testid="referral-source-link"
                                            >
                                                <div
                                                    className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[9px] font-bold flex-shrink-0"
                                                    style={{ backgroundColor: getCategoryColor(null) }}
                                                >
                                                    {detailContact.referred_by_contact.name?.charAt(0)?.toUpperCase()}
                                                </div>
                                                {detailContact.referred_by_contact.name}
                                            </button>
                                        </div>
                                    )}

                                    {/* Referrals (contacts received through this person) */}
                                    {detailContact.referrals?.length > 0 && (
                                        <div>
                                            <span className="text-[11px] text-muted-foreground">
                                                Kontakte durch {detailContact.name?.split(' ')[0]}:
                                            </span>
                                            <div className="flex flex-wrap gap-1.5 mt-1">
                                                {detailContact.referrals.map(ref => (
                                                    <button
                                                        key={ref.id}
                                                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/20 transition-colors"
                                                        onClick={() => openContactDetail(ref)}
                                                        data-testid={`referral-chip-${ref.id}`}
                                                    >
                                                        <span className="w-4 h-4 rounded-full bg-primary/20 flex items-center justify-center text-[8px] font-bold flex-shrink-0">
                                                            {ref.name?.charAt(0)?.toUpperCase()}
                                                        </span>
                                                        {ref.name}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Actions */}
                            <div className="flex gap-2 pt-2">
                                <Button variant="outline" className="flex-1" size="sm"
                                    onClick={() => { setShowDetailModal(false); setEditingContact({...detailContact}); setShowEditModal(true); }}>
                                    <Edit2 className="w-3.5 h-3.5 mr-1.5" /> Bearbeiten
                                </Button>
                                <Button variant="destructive" size="sm" className="px-3"
                                    onClick={() => { setShowDetailModal(false); handleDelete(detailContact.id); }}>
                                    <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* ===== ADD CONTACT MODAL ===== */}
            <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
                <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto p-4 sm:p-6">
                    <DialogHeader className="pb-2">
                        <DialogTitle className="text-base">Kontakt erstellen</DialogTitle>
                    </DialogHeader>
                    <ContactFormFields data={newContact} setData={setNewContact} />
                    <div className="flex gap-2 pt-3 sticky bottom-0 bg-background pb-1">
                        <Button data-testid="cancel-add-contact" variant="outline" className="flex-1" size="sm" onClick={() => setShowAddModal(false)}>Abbrechen</Button>
                        <Button data-testid="submit-add-contact" className="flex-1" size="sm" onClick={handleCreate}>Erstellen</Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* ===== EDIT CONTACT MODAL ===== */}
            <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
                <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto p-4 sm:p-6">
                    <DialogHeader className="pb-2">
                        <DialogTitle className="text-base">Kontakt bearbeiten</DialogTitle>
                    </DialogHeader>
                    {editingContact && (
                        <>
                            <ContactFormFields data={editingContact} setData={setEditingContact} isEdit />
                            <div className="flex gap-2 pt-3 sticky bottom-0 bg-background pb-1">
                                <Button data-testid="cancel-edit-contact" variant="outline" className="flex-1" size="sm" onClick={() => setShowEditModal(false)}>Abbrechen</Button>
                                <Button data-testid="submit-edit-contact" className="flex-1" size="sm" onClick={handleUpdate}>Speichern</Button>
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* ===== IMPORT MODAL ===== */}
            <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
                <DialogContent className="max-w-sm p-4 sm:p-6">
                    <DialogHeader className="pb-2">
                        <DialogTitle className="text-base">Kontakte importieren</DialogTitle>
                    </DialogHeader>

                    <div className="space-y-3">
                        {/* Native Contact Picker (Android Chrome) */}
                        {importSupported && (
                            <Button data-testid="import-device-btn" className="w-full gap-2 h-12 text-base" onClick={handleDeviceImport}>
                                <Smartphone className="w-5 h-5" />
                                Kontakte vom Handy auswaehlen
                            </Button>
                        )}

                        {/* Mobile but no Contact Picker (iOS) */}
                        {isMobile && !importSupported && (
                            <div className="space-y-3">
                                <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20 space-y-2.5">
                                    <p className="text-sm font-semibold text-blue-600">Mehrere Kontakte importieren:</p>
                                    <ol className="text-xs text-blue-600 space-y-1.5 list-decimal list-inside">
                                        <li>Oeffne <a href="https://www.icloud.com/contacts" target="_blank" rel="noopener noreferrer" className="underline font-semibold">icloud.com/contacts</a></li>
                                        <li>Gewuenschte Kontakte antippen <span className="text-blue-400">(oder alle auswaehlen)</span></li>
                                        <li>Zahnrad-Symbol &rarr; <strong>vCard exportieren</strong></li>
                                        <li>Exportierte .vcf Datei hier hochladen</li>
                                    </ol>
                                </div>
                                <div>
                                    <label className="flex items-center justify-center gap-2 h-12 rounded-lg border-2 border-dashed border-primary/30 bg-primary/5 text-primary text-sm font-medium cursor-pointer active:bg-primary/10 transition-colors">
                                        <Upload className="w-4 h-4" />
                                        .vcf Datei auswaehlen
                                        <input data-testid="import-vcard-input" type="file" accept=".vcf,.vcard" onChange={handleFileImport} className="hidden" />
                                    </label>
                                </div>
                                <div className="p-2.5 rounded-lg bg-muted/50 space-y-1">
                                    <p className="text-[11px] text-muted-foreground font-medium">Einzelnen Kontakt:</p>
                                    <p className="text-[11px] text-muted-foreground">Kontakte-App &rarr; Kontakt oeffnen &rarr; Kontakt teilen &rarr; In Dateien sichern</p>
                                </div>
                            </div>
                        )}

                        {/* Divider only if native picker shown */}
                        {importSupported && (
                            <div className="relative">
                                <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>
                                <div className="relative flex justify-center text-xs uppercase">
                                    <span className="bg-background px-2 text-muted-foreground">oder</span>
                                </div>
                            </div>
                        )}

                        {/* vCard for desktop or as fallback after native picker */}
                        {(!isMobile || importSupported) && (
                            <div>
                                <Label className="text-xs">vCard-Datei (.vcf)</Label>
                                <Input data-testid="import-vcard-input-desktop" type="file" accept=".vcf,.vcard" onChange={handleFileImport} className="mt-1" />
                                {!isMobile && (
                                    <p className="text-[11px] text-muted-foreground mt-1.5">
                                        Tipp: <a href="https://www.icloud.com/contacts" target="_blank" rel="noopener noreferrer" className="underline text-primary">icloud.com/contacts</a> &rarr; Kontakte auswaehlen &rarr; Zahnrad &rarr; vCard exportieren
                                    </p>
                                )}
                            </div>
                        )}

                        {/* Desktop hint */}
                        {!isMobile && !importSupported && (
                            <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                                <p className="text-xs text-blue-500">
                                    <Smartphone className="w-3.5 h-3.5 inline mr-1" />
                                    Tipp: Oeffne diese Seite auf dem Handy um direkt aus den Kontakten zu importieren
                                </p>
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            {/* ===== SHARE MODAL ===== */}
            <Dialog open={showShareModal} onOpenChange={setShowShareModal}>
                <DialogContent className="max-w-md p-4 sm:p-6">
                    <DialogHeader className="pb-2">
                        <DialogTitle className="text-base">Kontakte teilen</DialogTitle>
                    </DialogHeader>

                    <div className="space-y-3">
                        <div>
                            <Label className="text-xs">Link-Name *</Label>
                            <Input data-testid="share-name-input" value={newShare.name} onChange={(e) => setNewShare({ ...newShare, name: e.target.value })} placeholder="z.B. Partner-Liste" />
                        </div>

                        <div>
                            <Label className="text-xs">Kontakte</Label>
                            <Select value={newShare.selectedContacts.length === 0 ? 'all' : 'selected'} onValueChange={(v) => {
                                if (v === 'all') setNewShare({ ...newShare, selectedContacts: [] });
                            }}>
                                <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Alle Kontakte</SelectItem>
                                    <SelectItem value="selected">Bestimmte auswaehlen</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        {newShare.selectedContacts.length > 0 || (
                            <div className="flex items-center gap-2">
                                <input type="checkbox" id="usePassword" checked={newShare.usePassword}
                                    onChange={(e) => setNewShare({ ...newShare, usePassword: e.target.checked })} />
                                <Label htmlFor="usePassword" className="cursor-pointer text-sm">Mit Passwort schuetzen</Label>
                            </div>
                        )}

                        {newShare.usePassword && (
                            <Input data-testid="share-password-input" type="password" value={newShare.password}
                                onChange={(e) => setNewShare({ ...newShare, password: e.target.value })} placeholder="Passwort eingeben" />
                        )}

                        <div className="flex gap-2 pt-2">
                            <Button variant="outline" className="flex-1" size="sm" onClick={() => setShowShareModal(false)}>Abbrechen</Button>
                            <Button data-testid="create-share-btn" className="flex-1" size="sm" onClick={handleCreateShare}>Link erstellen</Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* ===== CATEGORIES MODAL ===== */}
            <Dialog open={showCategoriesModal} onOpenChange={setShowCategoriesModal}>
                <DialogContent className="max-w-md p-4 sm:p-6">
                    <DialogHeader className="pb-2">
                        <DialogTitle className="flex items-center gap-2 text-base">
                            <Tag className="w-4 h-4 text-primary" />
                            Kategorien verwalten
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-3">
                        <div className="flex gap-2">
                            <Input data-testid="new-category-input" value={newCategory} onChange={(e) => setNewCategory(e.target.value)}
                                placeholder="Neue Kategorie..." className="h-9"
                                onKeyDown={(e) => e.key === 'Enter' && handleCreateCategory()} />
                            <Button data-testid="add-category-btn" size="sm" className="h-9" onClick={handleCreateCategory}><Plus className="w-4 h-4" /></Button>
                        </div>

                        <div className="space-y-1.5 max-h-[50vh] overflow-y-auto">
                            {categories.length === 0 ? (
                                <p className="text-sm text-muted-foreground text-center py-4">Noch keine Kategorien</p>
                            ) : (
                                categories.map(cat => (
                                    <div key={cat.name} className="flex items-center justify-between p-2 rounded-lg bg-muted/50">
                                        <span className="flex items-center gap-2">
                                            <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: cat.color }} />
                                            <span className="text-sm font-medium">{cat.name}</span>
                                        </span>
                                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive"
                                            onClick={() => handleDeleteCategory(cat.name)}>
                                            <Trash2 className="w-3 h-3" />
                                        </Button>
                                    </div>
                                ))
                            )}
                        </div>

                        <Button variant="outline" className="w-full" size="sm" onClick={() => setShowCategoriesModal(false)}>Schliessen</Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default ContactsPage;
